import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { fileToBase64, PlatesSection } from "@/components/entity/EntitySections";

/** /strata/:roman — detail + admin edit for one of the Seven Layers.
 *  Lookup is case-insensitive: /strata/iii and /strata/III both work. */
export default function StratumDetail() {
  const { roman } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [stratum, setStratum] = useState(null); // null=loading, false=404
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const key = (roman || "").toUpperCase();
    api.get(`/layers/${key}`)
      .then((r) => setStratum(r.data))
      .catch(() => setStratum(false));
  }, [roman]);

  const startEdit = () => { setDraft({ ...stratum }); setEditing(true); };
  const cancel = () => { setEditing(false); setDraft(null); };

  const save = async () => {
    setSaving(true);
    try {
      const { roman: _ignored, ...patch } = draft;
      const res = await api.put(`/layers/${stratum.roman}`, patch);
      setStratum(res.data);
      setEditing(false);
      toast.success("Stratum sealed.");
    } catch {
      toast.error("Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const onUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.2 * 1024 * 1024) {
      toast.error("Image too large (max ~1.2MB).");
      return;
    }
    const b64 = await fileToBase64(file);
    setDraft((d) => ({ ...d, images: [...(d.images || []), b64] }));
  };

  const removeImage = (idx) => {
    setDraft((d) => ({ ...d, images: d.images.filter((_, i) => i !== idx) }));
  };

  if (stratum === null)
    return <div className="text-[#555] text-xs tracking-widest uppercase">::: retrieving stratum :::</div>;
  if (stratum === false)
    return (
      <div className="border border-[#333] p-12 text-center">
        <div className="font-serif-display text-3xl mb-4">No such stratum.</div>
        <button className="tiv-btn" onClick={() => navigate("/")}>Return to surface</button>
      </div>
    );

  const view = editing ? draft : stratum;

  return (
    <div className="space-y-12" data-testid="stratum-detail-page">
      {/* Breadcrumb */}
      <div className="text-[0.65rem] tracking-[0.35em] uppercase text-[#A3A3A3]">
        <Link to="/" className="hover:text-[#E3E1D9]">↖ Surface</Link>
        <span className="mx-3 text-[#555]">/</span>
        <span>Stratum {stratum.roman}</span>
      </div>

      {/* Header */}
      <header className="border-b border-[#333] pb-10 space-y-5">
        <div className="flex flex-wrap items-baseline gap-4">
          <span className="text-[0.6rem] tracking-[0.4em] uppercase text-[#A3A3A3]">
            Stratum {stratum.roman}
          </span>
          <span className="tiv-stamp">{stratum.english || "—"}</span>
        </div>
        {editing ? (
          <>
            <input
              className="tiv-input text-4xl"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="lore name (e.g. Superbia)"
              data-testid="stratum-edit-name"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                className="tiv-input"
                value={draft.english}
                onChange={(e) => setDraft({ ...draft, english: e.target.value })}
                placeholder="English (Pride / Envy / Greed...)"
                data-testid="stratum-edit-english"
              />
              <input
                className="tiv-input"
                value={draft.sin}
                onChange={(e) => setDraft({ ...draft, sin: e.target.value })}
                placeholder="sin epigram (one-liner)"
                data-testid="stratum-edit-sin"
              />
            </div>
          </>
        ) : (
          <>
            <h1 className="font-serif-display text-5xl sm:text-6xl tracking-tighter leading-none">
              {stratum.name || <span className="text-[#555] italic">[ unnamed ]</span>}
            </h1>
            {stratum.sin && (
              <p className="text-sm text-[#A3A3A3] max-w-2xl leading-relaxed">{stratum.sin}</p>
            )}
          </>
        )}
      </header>

      {/* Description */}
      <Section label="Description · The Stratum Itself" tag="§ folio">
        {editing ? (
          <textarea
            className="tiv-textarea"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            placeholder="what this layer IS — geography, weather, the law that binds it..."
            data-testid="stratum-edit-description"
          />
        ) : view.description ? (
          <div className="text-base leading-loose whitespace-pre-wrap max-w-3xl">{view.description}</div>
        ) : (
          <Empty label="no description recorded" />
        )}
      </Section>

      {/* Appearance */}
      <Section label="Appearance · How It Looks" tag="visus">
        {editing ? (
          <textarea
            className="tiv-textarea"
            value={draft.appearance}
            onChange={(e) => setDraft({ ...draft, appearance: e.target.value })}
            placeholder="light, colour, surfaces, sound, smell..."
            data-testid="stratum-edit-appearance"
          />
        ) : view.appearance ? (
          <div className="text-base leading-loose whitespace-pre-wrap max-w-3xl">{view.appearance}</div>
        ) : (
          <Empty label="appearance unrecorded" />
        )}
      </Section>

      {/* Inhabitants */}
      <Section label="Inhabitants · The Population" tag="incolae">
        {editing ? (
          <textarea
            className="tiv-textarea"
            value={draft.inhabitants}
            onChange={(e) => setDraft({ ...draft, inhabitants: e.target.value })}
            placeholder="who walks here — demons, the damned, intermediaries..."
            data-testid="stratum-edit-inhabitants"
          />
        ) : view.inhabitants ? (
          <div className="text-base leading-loose whitespace-pre-wrap max-w-3xl">{view.inhabitants}</div>
        ) : (
          <Empty label="no inhabitants catalogued" />
        )}
      </Section>

      {/* Notes (mono, italic, marginalia feel) */}
      <Section label="Marginalia · Archivist Notes" tag="notae">
        {editing ? (
          <textarea
            className="tiv-textarea"
            value={draft.notes}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            placeholder="footnotes, contradictions, sightings..."
            data-testid="stratum-edit-notes"
          />
        ) : view.notes ? (
          <div className="text-sm leading-loose whitespace-pre-wrap font-mono-data text-[#A3A3A3] max-w-3xl border-l-2 border-[#333] pl-4">
            {view.notes}
          </div>
        ) : (
          <Empty label="no marginalia" />
        )}
      </Section>

      {/* Plates — reuse the entity component */}
      <PlatesSection
        images={view.images}
        editing={editing}
        onUploadImage={onUploadImage}
        removeImage={removeImage}
        testidPrefix="stratum"
      />

      {/* Admin action bar */}
      {isAdmin && (
        <div className="flex flex-wrap gap-3 border-t border-[#333] pt-8">
          {editing ? (
            <>
              <button className="tiv-btn" onClick={save} disabled={saving} data-testid="stratum-save-btn">
                {saving ? "Sealing..." : "Seal & Save"}
              </button>
              <button className="tiv-btn" onClick={cancel} data-testid="stratum-cancel-btn">
                Discard
              </button>
            </>
          ) : (
            <button className="tiv-btn" onClick={startEdit} data-testid="stratum-edit-btn">
              Edit Stratum
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ label, tag, children }) {
  return (
    <section>
      <div className="section-label">
        <span>{label}</span>
        <span>{tag}</span>
      </div>
      {children}
    </section>
  );
}

function Empty({ label }) {
  return (
    <div className="border border-dashed border-[#333] p-10 text-center text-[#555] text-xs tracking-widest uppercase">
      ::: {label} :::
    </div>
  );
}
